import {
  ClickAwayListener,
  Grid,
  Paper,
  Popper,
  Typography,
  Button,
  Box,
} from '@mui/material';
import { Link as RouterLink } from 'react-router';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import PredictiveSearchResultsList from './PredictiveSearchResultsList';
import ContentSearchResultsList from './ContentSearchResultsList';
import { getFormattedSearchValue, minSearchLength } from './SearchUtils';
import usePredictiveSearch from '../../hooks/usePredictiveSearch';
import { JaaRoute } from '../../utilities/Links';
import { useSearchArticles } from '../../hooks/useSearchArticles';
import { useDebounce } from '../../hooks/useDebounce';
import { debounceDelay } from './constants';

interface PredictiveSearchResultsDropdownProps {
  readonly open: boolean;
  readonly search: string;
  readonly anchorEl: HTMLElement | null;
  readonly onClickAway?: () => void;
}

export default function PredictiveSearchResultsDropdown({
  open,
  search,
  anchorEl,
  onClickAway = () => {},
}: PredictiveSearchResultsDropdownProps) {
  const { t } = useTranslation();
  const debouncedSearch = useDebounce(search, debounceDelay);
  const { matchingAuthors, matchingCategories } = usePredictiveSearch(
    getFormattedSearchValue(search),
  );

  // Get article count for the footer
  const { totalArticleCount } = useSearchArticles({
    minSearchLength,
    search: debouncedSearch,
    itemsPerPage: 10,
  });

  const totalResults =
    totalArticleCount + matchingAuthors.length + matchingCategories.length;

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      disablePortal
      placement='bottom-start'
      modifiers={[
        // Copied from: https://github.com/floating-ui/floating-ui/issues/794#issuecomment-589253942
        {
          name: 'sameWidth',
          enabled: true,
          phase: 'beforeWrite',
          requires: ['computeStyles'],
          fn({ state }) {
            state.styles.popper.minWidth = `${state.rects.reference.width}px`;
            state.styles.popper.maxWidth = `${state.rects.reference.width}px`;
          },
        },
      ]}
      sx={{ zIndex: 1500 }}
    >
      <ClickAwayListener onClickAway={onClickAway}>
        <Paper
          sx={{ width: '100%', padding: 3, paddingBottom: 1.5 }}
          role='presentation'
        >
          <Grid container columnSpacing={3}>
            <Grid size={{ xs: 8 }}>
              <ContentSearchResultsList search={search} />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <PredictiveSearchResultsList
                authors={matchingAuthors}
                categories={matchingCategories}
                maxAuthors={5}
              />
            </Grid>
          </Grid>

          {/* Footer with total results count */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: 2,
              paddingX: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
              marginTop: 2,
            }}
          >
            <Typography variant='body2' color='text.secondary'>
              {t('search.resultCount', { count: totalResults })}
            </Typography>
            {search && (
              <Button
                component={RouterLink}
                to={`${JaaRoute.articleSearch}?value=${encodeURIComponent(search)}`}
                variant='text'
                size='small'
                endIcon={<ArrowForwardIcon />}
              >
                {t('search.showAllResults')}
              </Button>
            )}
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
}
